import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X, Search, RotateCcw, Loader2, Move } from 'lucide-react'
import { RootState } from '@/store/store'
import { previewUrl } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'
import {
  setCropBox,
  confirmRegionSearch,
  cancelRegionSelection,
  setSearchResults,
  setSearchError,
} from '@/store/slices/searchSlice'
import { closeImageModal } from '@/store/slices/uiSlice'
import { useSearchByRegionMutation, CropBox } from '@/store/api'

interface Point {
  x: number
  y: number
}

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w' | null

function RegionSelector() {
  useSignedUrls()
  const dispatch = useDispatch()
  const { isSelectingRegion, regionSourceImageId, cropBox } = useSelector(
    (state: RootState) => state.search
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null)
  const [startPoint, setStartPoint] = useState<Point | null>(null)
  const [currentBox, setCurrentBox] = useState<CropBox | null>(cropBox)
  const [dragOffset, setDragOffset] = useState<Point | null>(null)
  const [imageLoaded, setImageLoaded] = useState(false)

  const [triggerRegionSearch, { isLoading: isSearching }] = useSearchByRegionMutation()

  // Reset state when component mounts
  useEffect(() => {
    setCurrentBox(null)
    setImageLoaded(false)
  }, [regionSourceImageId])

  // Convert pixel coordinates to percentage (0-1)
  const pixelToPercent = useCallback((px: Point): Point => {
    if (!imageRef.current) return { x: 0, y: 0 }
    const rect = imageRef.current.getBoundingClientRect()
    return {
      x: Math.max(0, Math.min(1, px.x / rect.width)),
      y: Math.max(0, Math.min(1, px.y / rect.height)),
    }
  }, [])

  // Get mouse position relative to image
  const getMousePosition = useCallback((e: React.MouseEvent | MouseEvent): Point => {
    if (!imageRef.current) return { x: 0, y: 0 }
    const rect = imageRef.current.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
  }, [])

  // Handle mouse down - start drawing, dragging, or resizing
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!imageRef.current) return
    e.preventDefault()

    const pos = getMousePosition(e)
    const pctPos = pixelToPercent(pos)

    // Check if clicking on resize handle
    if (currentBox) {
      const handle = getResizeHandle(pos, currentBox)
      if (handle) {
        setIsResizing(true)
        setResizeHandle(handle)
        setStartPoint(pos)
        return
      }

      // Check if clicking inside existing box (drag)
      if (isInsideBox(pctPos, currentBox)) {
        setIsDragging(true)
        setDragOffset({
          x: pctPos.x - currentBox.x,
          y: pctPos.y - currentBox.y,
        })
        return
      }
    }

    // Start drawing new box
    setIsDrawing(true)
    setStartPoint(pos)
    setCurrentBox(null)
  }, [currentBox, getMousePosition, pixelToPercent])

  // Check if point is inside box
  const isInsideBox = (point: Point, box: CropBox): boolean => {
    return (
      point.x >= box.x &&
      point.x <= box.x + box.width &&
      point.y >= box.y &&
      point.y <= box.y + box.height
    )
  }

  // Get resize handle at position
  const getResizeHandle = (pos: Point, box: CropBox): ResizeHandle => {
    if (!imageRef.current) return null
    const handleSize = 12
    const rect = imageRef.current.getBoundingClientRect()

    // Use rendered dimensions, not natural dimensions
    const boxPixel = {
      x: box.x * rect.width,
      y: box.y * rect.height,
      width: box.width * rect.width,
      height: box.height * rect.height,
    }

    const corners = {
      nw: { x: boxPixel.x, y: boxPixel.y },
      ne: { x: boxPixel.x + boxPixel.width, y: boxPixel.y },
      sw: { x: boxPixel.x, y: boxPixel.y + boxPixel.height },
      se: { x: boxPixel.x + boxPixel.width, y: boxPixel.y + boxPixel.height },
    }

    for (const [handle, corner] of Object.entries(corners)) {
      if (
        Math.abs(pos.x - corner.x) < handleSize &&
        Math.abs(pos.y - corner.y) < handleSize
      ) {
        return handle as ResizeHandle
      }
    }

    return null
  }

  // Handle mouse move
  const handleMouseMove = useCallback((e: React.MouseEvent | MouseEvent) => {
    if (!imageRef.current) return

    const pos = getMousePosition(e)
    const pctPos = pixelToPercent(pos)

    if (isDrawing && startPoint) {
      const startPct = pixelToPercent(startPoint)
      const newBox: CropBox = {
        x: Math.min(startPct.x, pctPos.x),
        y: Math.min(startPct.y, pctPos.y),
        width: Math.abs(pctPos.x - startPct.x),
        height: Math.abs(pctPos.y - startPct.y),
      }
      setCurrentBox(newBox)
    } else if (isDragging && currentBox && dragOffset) {
      const newX = Math.max(0, Math.min(1 - currentBox.width, pctPos.x - dragOffset.x))
      const newY = Math.max(0, Math.min(1 - currentBox.height, pctPos.y - dragOffset.y))
      setCurrentBox({
        ...currentBox,
        x: newX,
        y: newY,
      })
    } else if (isResizing && currentBox && resizeHandle && startPoint) {
      const newBox = { ...currentBox }
      const startPct = pixelToPercent(startPoint)
      const delta = {
        x: pctPos.x - startPct.x,
        y: pctPos.y - startPct.y,
      }

      switch (resizeHandle) {
        case 'nw':
          newBox.x = Math.max(0, currentBox.x + delta.x)
          newBox.y = Math.max(0, currentBox.y + delta.y)
          newBox.width = Math.max(0.05, currentBox.width - delta.x)
          newBox.height = Math.max(0.05, currentBox.height - delta.y)
          break
        case 'ne':
          newBox.y = Math.max(0, currentBox.y + delta.y)
          newBox.width = Math.max(0.05, currentBox.width + delta.x)
          newBox.height = Math.max(0.05, currentBox.height - delta.y)
          break
        case 'sw':
          newBox.x = Math.max(0, currentBox.x + delta.x)
          newBox.width = Math.max(0.05, currentBox.width - delta.x)
          newBox.height = Math.max(0.05, currentBox.height + delta.y)
          break
        case 'se':
          newBox.width = Math.max(0.05, currentBox.width + delta.x)
          newBox.height = Math.max(0.05, currentBox.height + delta.y)
          break
      }

      // Clamp to image bounds
      newBox.width = Math.min(newBox.width, 1 - newBox.x)
      newBox.height = Math.min(newBox.height, 1 - newBox.y)

      setCurrentBox(newBox)
      setStartPoint(pos)
    }
  }, [isDrawing, isDragging, isResizing, startPoint, currentBox, dragOffset, resizeHandle, getMousePosition, pixelToPercent])

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    if (isDrawing && currentBox) {
      // Minimum size check (at least 5% of image)
      if (currentBox.width < 0.05 || currentBox.height < 0.05) {
        setCurrentBox(null)
      } else {
        dispatch(setCropBox(currentBox))
      }
    } else if (isDragging || isResizing) {
      if (currentBox) {
        dispatch(setCropBox(currentBox))
      }
    }

    setIsDrawing(false)
    setIsDragging(false)
    setIsResizing(false)
    setResizeHandle(null)
    setStartPoint(null)
    setDragOffset(null)
  }, [isDrawing, isDragging, isResizing, currentBox, dispatch])

  // Global mouse events
  useEffect(() => {
    if (isDrawing || isDragging || isResizing) {
      const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e)
      const handleGlobalMouseUp = () => handleMouseUp()

      window.addEventListener('mousemove', handleGlobalMouseMove)
      window.addEventListener('mouseup', handleGlobalMouseUp)

      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove)
        window.removeEventListener('mouseup', handleGlobalMouseUp)
      }
    }
  }, [isDrawing, isDragging, isResizing, handleMouseMove, handleMouseUp])

  // Handle cancel
  const handleCancel = useCallback(() => {
    dispatch(cancelRegionSelection())
  }, [dispatch])

  // Handle reset selection
  const handleReset = useCallback(() => {
    setCurrentBox(null)
    dispatch(setCropBox(null))
  }, [dispatch])

  // Handle search
  const handleSearch = useCallback(async () => {
    if (!currentBox || !regionSourceImageId) return

    dispatch(confirmRegionSearch())
    dispatch(closeImageModal())

    try {
      const results = await triggerRegionSearch({
        masterId: regionSourceImageId,
        cropBox: currentBox,
        limit: 20,
      }).unwrap()
      dispatch(setSearchResults(results))
    } catch (error) {
      dispatch(setSearchError(error instanceof Error ? error.message : 'Region search failed'))
    }
  }, [currentBox, regionSourceImageId, dispatch, triggerRegionSearch])

  // Handle keyboard
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel()
      } else if (e.key === 'Enter' && currentBox) {
        handleSearch()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleCancel, handleSearch, currentBox])

  if (!isSelectingRegion || !regionSourceImageId) return null

  const imgPreviewUrl = previewUrl(regionSourceImageId)

  // Calculate box style
  const getBoxStyle = () => {
    if (!currentBox || !imageRef.current) return {}
    return {
      left: `${currentBox.x * 100}%`,
      top: `${currentBox.y * 100}%`,
      width: `${currentBox.width * 100}%`,
      height: `${currentBox.height * 100}%`,
    }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50">
        <div className="flex items-center gap-3">
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} className="text-white" />
          </button>
          <div>
            <h2 className="text-white font-medium">Select Region</h2>
            <p className="text-white/60 text-sm">
              {currentBox ? 'Adjust selection or click Search' : 'Click and drag to select an area'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {currentBox && (
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <RotateCcw size={16} />
              Reset
            </button>
          )}
          <button
            onClick={handleSearch}
            disabled={!currentBox || isSearching}
            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSearching ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Search size={16} />
            )}
            Search Region
          </button>
        </div>
      </div>

      {/* Image Container */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center p-8 overflow-hidden"
      >
        <div className="relative max-w-full max-h-full">
          {/* Image */}
          <img
            ref={imageRef}
            src={imgPreviewUrl}
            alt="Select region"
            onLoad={() => setImageLoaded(true)}
            className={`max-w-full max-h-[calc(100vh-160px)] object-contain select-none ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            } transition-opacity`}
            draggable={false}
          />

          {/* Overlay and Selection - only show when image loaded */}
          {imageLoaded && (
            <div
              className={`absolute inset-0 ${
                !currentBox ? 'cursor-crosshair' : isDragging ? 'cursor-grabbing' : 'cursor-default'
              }`}
              onMouseDown={handleMouseDown}
            >
              {/* Dark overlay outside selection */}
              {currentBox && (
                <>
                  {/* Top */}
                  <div
                    className="absolute bg-black/60"
                    style={{
                      top: 0,
                      left: 0,
                      right: 0,
                      height: `${currentBox.y * 100}%`,
                    }}
                  />
                  {/* Bottom */}
                  <div
                    className="absolute bg-black/60"
                    style={{
                      top: `${(currentBox.y + currentBox.height) * 100}%`,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    }}
                  />
                  {/* Left */}
                  <div
                    className="absolute bg-black/60"
                    style={{
                      top: `${currentBox.y * 100}%`,
                      left: 0,
                      width: `${currentBox.x * 100}%`,
                      height: `${currentBox.height * 100}%`,
                    }}
                  />
                  {/* Right */}
                  <div
                    className="absolute bg-black/60"
                    style={{
                      top: `${currentBox.y * 100}%`,
                      left: `${(currentBox.x + currentBox.width) * 100}%`,
                      right: 0,
                      height: `${currentBox.height * 100}%`,
                    }}
                  />
                </>
              )}

              {/* Selection Box */}
              {currentBox && (
                <div
                  className="absolute border-2 border-primary"
                  style={getBoxStyle()}
                >
                  {/* Drag indicator in center */}
                  <div className="absolute inset-0 flex items-center justify-center cursor-grab active:cursor-grabbing">
                    <div className="p-2 bg-black/50 rounded-lg opacity-0 hover:opacity-100 transition-opacity">
                      <Move size={16} className="text-white" />
                    </div>
                  </div>

                  {/* Corner resize handles */}
                  {['nw', 'ne', 'sw', 'se'].map((corner) => (
                    <div
                      key={corner}
                      className={`absolute w-4 h-4 bg-primary rounded-sm border-2 border-white ${
                        corner === 'nw' ? '-top-2 -left-2 cursor-nwse-resize' :
                        corner === 'ne' ? '-top-2 -right-2 cursor-nesw-resize' :
                        corner === 'sw' ? '-bottom-2 -left-2 cursor-nesw-resize' :
                        '-bottom-2 -right-2 cursor-nwse-resize'
                      }`}
                    />
                  ))}

                  {/* Size indicator */}
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/70 rounded text-xs text-white whitespace-nowrap">
                    {Math.round(currentBox.width * 100)}% x {Math.round(currentBox.height * 100)}%
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Loading state */}
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="flex items-center justify-center gap-6 p-4 bg-black/50 text-white/60 text-sm">
        <span>
          <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-xs">Click + Drag</kbd> to select
        </span>
        <span>
          <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-xs">Corners</kbd> to resize
        </span>
        <span>
          <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-xs">Enter</kbd> to search
        </span>
        <span>
          <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-xs">Esc</kbd> to cancel
        </span>
      </div>
    </div>
  )
}

export default RegionSelector
